import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ShieldCheck } from 'lucide-react';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Get started with the essentials',
    prices: [
      { label: 'USD', amount: '$0' },
      { label: 'AED', amount: '0 AED' },
      { label: 'SAR', amount: '0 SAR' },
      { label: 'BHD', amount: '0 BHD' }
    ],
    billingNote: 'Forever free • Perfect for curious learners exploring AXORA.',
    features: [
      '50 chat prompts per month with helpful guidance',
      'Generate 2 high-quality images for moodboards',
      '1 GB secure storage for notes and uploads',
      'Access to all free courses and learning paths',
      'Community access (view-only mode)'
    ],
    cta: { label: 'Start For Free', to: '/signup?plan=free' }
  },
  {
    id: 'learner-plus',
    name: 'Learner Plus',
    tagline: 'Elevate your learning experience',
    prices: [
      { label: 'USD', amount: '$12', note: '$120 billed annually' },
      { label: 'AED', amount: '45 AED', note: '450 AED billed annually' },
      { label: 'SAR', amount: '45 SAR', note: '450 SAR billed annually' },
      { label: 'BHD', amount: '4.5 BHD', note: '45 BHD billed annually' }
    ],
    billingNote: 'Ideal for students and hobbyists who need more power.',
    features: [
      'Up to 2 million chat tokens for deep conversations',
      '30 pro-quality image generations every month',
      '10 GB cloud storage for course files and references',
      'Exclusive course bundles and member discounts',
      'Basic CPD (continuing professional development) tracking'
    ],
    highlight: true,
    cta: { label: 'Upgrade to Learner Plus', to: '/signup?plan=learner-plus' }
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Built for practicing professionals',
    prices: [
      { label: 'USD', amount: '$29', note: '$290 billed annually' },
      { label: 'AED', amount: '110 AED', note: '1,100 AED billed annually' },
      { label: 'SAR', amount: '110 SAR', note: '1,100 SAR billed annually' },
      { label: 'BHD', amount: '11 BHD', note: '110 BHD billed annually' }
    ],
    billingNote: 'Includes professional-grade tools and support.',
    features: [
      'Up to 6 million chat tokens for advanced workflows',
      '100 AI image renders for presentations and design studies',
      '50 GB storage with version history and secure sharing',
      'Advanced CPD ledger with export-ready reports',
      'AXORA Studio access, Pin-Up showcase, and priority support',
      'Monthly allocation of premium CPD courses'
    ],
    cta: { label: 'Unlock Pro Tools', to: '/signup?plan=pro' }
  }
];

const OVERAGE_NOTICE = [
  'Need more capacity? Add-ons are available any time:',
  '• Additional 1M chat tokens',
  '• Extra image generation packs',
  '• Extra cloud storage per GB'
];

const Pricing = () => {
  const handlePlanSelection = (planId) => {
    localStorage.setItem('selectedPlan', planId);
    localStorage.setItem('subscriptionStatus', 'pending-payment');
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <header className="border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-5">
          <Link to="/" className="text-2xl font-semibold tracking-wide text-[#AC5757]">
            AXORA
          </Link>

          <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
            <a href="/#features" className="hover:text-[#AC5757]">
              Features
            </a>
            <a href="/#opportunities" className="hover:text-[#AC5757]">
              Opportunities
            </a>
            <a href="/#faq" className="hover:text-[#AC5757]">
              FAQs
            </a>
            <a href="/#about" className="hover:text-[#AC5757]">
              About
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-[#AC5757]">
              Sign in
            </Link>
            <Link
              to="/signup?plan=free"
              onClick={() => handlePlanSelection('free')}
              className="rounded-full bg-[#AC5757] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#8A4A4A]"
            >
              Join Axora
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16 space-y-20">
        <section className="text-center space-y-5">
          <span className="text-xs uppercase tracking-[0.4em] text-[#AC5757]">Pricing</span>
          <h1 className="text-4xl md:text-5xl font-judson font-bold">
            Plans built for every stage of your professional journey
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
            Whether you’re exploring architecture and engineering, leveling up your skills, or managing full client
            portfolios, Axora keeps you equipped with curated learning, collaboration tools, and measurable CPD growth.
          </p>
        </section>

        <section className="grid gap-10 md:grid-cols-2 xl:grid-cols-3">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`flex flex-col rounded-3xl border bg-white px-6 pt-8 pb-6 shadow-sm transition-transform hover:-translate-y-1 hover:shadow-lg ${
                plan.highlight ? 'border-[#AC5757] shadow-xl' : 'border-gray-200'
              }`}
            >
              <div className="space-y-3 border-b border-gray-200 pb-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm uppercase tracking-[0.2em] text-gray-400">{plan.id}</p>
                  {plan.highlight && (
                    <span className="rounded-full bg-[#AC5757]/10 px-3 py-1 text-xs font-semibold uppercase text-[#AC5757]">
                      Most Popular
                    </span>
                  )}
                </div>
                <h2 className="text-2xl font-semibold">{plan.name}</h2>
                <p className="text-sm text-gray-500">{plan.tagline}</p>
              </div>

              <div className="space-y-3 border-b border-gray-200 py-6">
                {plan.prices.map((price) => (
                  <div key={price.label} className="flex flex-wrap items-baseline gap-2">
                    <span className="text-2xl font-semibold text-[#AC5757]">{price.amount}</span>
                    <span className="text-sm font-medium text-gray-500">{price.label}</span>
                    {price.note && <span className="text-xs text-gray-400">({price.note})</span>}
                  </div>
                ))}
                <p className="text-xs text-gray-400">{plan.billingNote}</p>
              </div>

              <div className="flex-1 space-y-3 py-6">
                <ul className="space-y-3 text-sm text-gray-600">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 leading-relaxed">
                      <CheckCircle size={18} className="mt-[2px] text-[#AC5757]" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-auto space-y-3">
                <Link
                  to={plan.cta.to}
                  onClick={() => handlePlanSelection(plan.id)}
                  className={`block w-full rounded-full px-5 py-3 text-center text-sm font-semibold transition-colors ${
                    plan.highlight
                      ? 'bg-[#AC5757] text-white hover:bg-[#8A4A4A]'
                      : 'border border-[#AC5757] text-[#AC5757] hover:bg-[#AC5757]/10'
                  }`}
                >
                  {plan.cta.label}
                </Link>
                <div className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs text-gray-500">
                  <ShieldCheck size={14} className="text-[#AC5757]" />
                  <span>Secure checkout with encrypted payment processing</span>
                </div>
              </div>
            </div>
          ))}
        </section>

        <section className="rounded-3xl border border-gray-200 bg-white p-8 md:p-10">
          <h3 className="text-xl font-semibold text-gray-900">Need more flexibility?</h3>
          <ul className="mt-4 space-y-2 text-sm text-gray-600">
            {OVERAGE_NOTICE.map((line, idx) => (
              <li key={idx}>{line}</li>
            ))}
          </ul>
        </section>

        <section className="rounded-3xl bg-[#AC5757] px-8 py-12 text-white text-center shadow-lg">
          <h3 className="text-3xl font-judson font-bold">Ready to grow with Axora?</h3>
          <p className="mt-4 text-base md:text-lg text-[#F4EDED] max-w-3xl mx-auto">
            Join thousands of engineers, architects, and educators who trust Axora to keep their skills sharp, portfolios
            polished, and CPD journeys on track.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/signup?plan=free"
              onClick={() => handlePlanSelection('free')}
              className="rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#AC5757] shadow hover:bg-[#FDF7F7]"
            >
              Create your account
            </Link>
            <Link
              to="/login"
              className="rounded-full border border-white/60 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              I already have access
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Pricing;

